<template>
  <section class="UserInvitation">
    <main class="UserInvitation__main">
      <Card class="UserInvitation__card">
        <div class="UserInvitation__descriptionMain">
          <section>
            <Heading
              v-if="mode == 'ADD'"
              class="UserInvitation__info"
              type="tertiary"
              level="2"
              :heading="messages.user.userInvitation.info.userInviteDesc"
            />
            <template v-if="isSkeleton">
              <ul v-for="n in 8" :key="n">
                <li colspan="2"><TableSkeletonItem type="table" /></li>
              </ul>
            </template>
            <template v-else>
              <ul>
                <li
                  v-for="(item, index) in formStateReact.value"
                  :key="index"
                  class="UserInvitation__list"
                  :class="[
                    {
                      'UserInvitation__list--editMode': mode !== 'ADD',
                    },
                  ]"
                >
                  <div class="UserInvitation__inner">
                    <div class="UserInvitation__userTitle">
                      <Heading
                        v-if="mode == 'ADD'"
                        class="UserInvitation__info"
                        type="tertiary"
                        level="2"
                        :heading="
                          messages.user.userInvitation.invite + (index + 1)
                        "
                      />
                      <CancelButton
                        v-if="index !== 0"
                        @click="onClickRemove(index)"
                      />
                    </div>
                    <div class="UserInvitation__userSubTitle">
                      <Heading
                        class="UserInvitation__info"
                        type="tertiary"
                        level="2"
                        :heading="messages.user.userInvitation.basicInformation"
                      />
                    </div>
                    <table class="UserInvitation__table">
                      <tbody>
                        <tr v-if="mode !== 'ADD'">
                          <th class="UserInvitation__heading" scope="col">
                            <span class="UserInvitation__label">
                              {{ messages.common.headList.name }}
                            </span>
                          </th>
                          <td class="UserInvitation__item">
                            <span>{{ item.userName || '-' }}</span>
                          </td>
                        </tr>

                        <tr v-if="mode !== 'ADD'">
                          <th class="UserInvitation__heading" scope="col">
                            <span class="UserInvitation__label"
                              >{{ messages.common.headList.displayName }}
                              <TypeLabel v-if="mode === 'EDIT'" isRequired />
                            </span>
                          </th>
                          <td class="UserInvitation__item">
                            <TextField
                              v-if="mode == 'EDIT' || mode == 'ADD'"
                              id="userName"
                              class="UserInvitation__textField"
                              type="text"
                              name="userName"
                              :ariaLabel="messages.common.headList.displayName"
                              :placeholder="
                                messages.common.headList.displayName
                              "
                              :isError="
                                (item.validate?.displayNameError ?? '') !== ''
                              "
                              :errorText="item.validate?.displayNameError"
                              :value="item.displayName"
                              @input="item.displayName = $event.target.value"
                              @blur="
                                validateDisplayName(item.displayName, index)
                              "
                            />
                            <span v-else>{{ item.displayName || '-' }}</span>
                          </td>
                        </tr>

                        <tr>
                          <th class="UserInvitation__heading" scope="col">
                            <span class="UserInvitation__label"
                              >{{ messages.common.headList.email }}
                              <TypeLabel
                                v-if="mode === 'EDIT' || mode === 'ADD'"
                                isRequired
                              />
                            </span>
                          </th>
                          <td class="UserInvitation__item">
                            <TagInput
                              v-if="mode === 'ADD'"
                              id="email"
                              v-model:modelValues="item.emailIds"
                              class="UserInvitation__textField"
                              :placeholder="
                                messages.user.userInvitation.placeholder.email
                              "
                              name="email"
                              :separators="['Enter', ',']"
                              :isError="
                                (item.validate?.emailError ?? '') !== ''
                              "
                              :errorText="item.validate?.emailError ?? ''"
                              @update:modelValues="validateEmail($event, index)"
                              @update:isInvalidMailPresent="
                                event => setInvalidMailError(index, event)
                              "
                            />
                            <TextField
                              v-if="mode === 'EDIT'"
                              id="email"
                              class="UserInvitation__textField"
                              type="text"
                              name="email"
                              :ariaLabel="
                                messages.user.userInvitation.emailAriaLabel
                              "
                              :placeholder="
                                messages.user.userInvitation.placeholder.email
                              "
                              :isError="
                                (item.validate?.emailError ?? '') !== ''
                              "
                              :errorText="item.validate?.emailError"
                              :value="item.emailIds[0] ?? ''"
                              @blur="validateEmailId()"
                              @input="updateMailId($event.target.value, index)"
                            />
                            <span
                              v-if="mode === 'ADD'"
                              class="UserInvitation__note"
                              >{{
                                messages.user.userInvitation.info.email
                              }}</span
                            >
                            <span v-if="mode === 'VIEW'">{{
                              item.emailIds[0] || '-'
                            }}</span>
                          </td>
                        </tr>

                        <tr>
                          <th class="UserInvitation__heading" scope="col">
                            <span class="UserInvitation__label">
                              {{ messages.common.headList.authority }}
                              <TypeLabel
                                v-if="mode === 'EDIT' || mode === 'ADD'"
                                isRequired
                              />
                            </span>
                          </th>
                          <td class="UserInvitation__item">
                            <DropDown
                              v-if="mode == 'EDIT' || mode == 'ADD'"
                              id="authority"
                              :value="item.authority"
                              class="UserInvitation__dropDown"
                              :options="item.authorityOptions"
                              :isError="
                                (item.validate?.authorityError ?? '') !== ''
                              "
                              :errorText="item.validate?.authorityError"
                              :placeholder="
                                messages.user.userInvitation.placeholder
                                  .unSelected
                              "
                              @change="onChangeAuthority($event, index)"
                            />
                            <span v-else>{{
                              getAuthorityText(item.authority)
                            }}</span>
                          </td>
                        </tr>
                        <tr>
                          <th class="UserInvitation__heading" scope="col">
                            <span class="UserInvitation__label">
                              {{ messages.common.headList.groupName }}
                              <TypeLabel
                                v-if="mode === 'EDIT' || mode === 'ADD'"
                                isRequired
                              />
                            </span>
                          </th>
                          <td
                            v-if="mode == 'EDIT' || mode == 'ADD'"
                            class="UserInvitation__item"
                          >
                            <ul>
                              <li
                                v-for="(group, groupIndex) in item.groupIds"
                                :key="groupIndex"
                                class="UserInvitation__dropDownDisplayList"
                              >
                                <div class="UserInvitation__dropDownList">
                                  <DropDown
                                    id="group"
                                    :value="group.value"
                                    class="UserInvitation__dropDown"
                                    :options="item.groupOptions"
                                    :placeholder="
                                      messages.user.userInvitation.placeholder
                                        .unSelected
                                    "
                                    :isError="
                                      (item.validate?.groupIdsError?.[
                                        groupIndex
                                      ] ?? '') !== ''
                                    "
                                    @change="
                                      onChangeGroup($event, index, groupIndex)
                                    "
                                  />
                                  <CancelButton
                                    v-if="groupIndex !== 0"
                                    @click="
                                      onClickRemoveGroup(index, groupIndex)
                                    "
                                  />
                                </div>
                                <span class="UserInvitation__errorText">
                                  {{
                                    item.validate?.groupIdsError?.[groupIndex]
                                  }}
                                </span>
                              </li>
                            </ul>

                            <Button
                              class="UserInvitation__addButton"
                              tag="button"
                              dynamics="secondary"
                              :label="messages.common.add"
                              icon-name="Add"
                              :isDisabled="
                                (item.groupOptions?.length ?? 0) == 0 ||
                                (item.groupOptions?.length ?? 0) <=
                                  formStateReact.value[index].groupIds.length
                              "
                              @click="onClickAddGroup(index)"
                            />
                          </td>
                          <td v-else class="UserInvitation__item">
                            <span>
                              <template v-if="item.groupIds?.length">
                                <span
                                  v-for="(group, groupIdIndex) in item.groupIds"
                                  :key="groupIdIndex"
                                >
                                  {{ group.text }}<br />
                                </span>
                              </template>
                              <span v-else>-</span>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th
                            v-if="mode === 'VIEW'"
                            class="UserInvitation__heading"
                            scope="col"
                          >
                            <span class="UserInvitation__label">
                              {{ messages.user.userInvitation.regDateTime }}
                            </span>
                          </th>
                          <td
                            v-if="mode === 'VIEW'"
                            class="UserInvitation__item"
                          >
                            <span>{{ item.createdAt }}</span>
                          </td>
                        </tr>
                        <tr v-if="mode === 'EDIT' || mode === 'VIEW'">
                          <th class="UserInvitation__heading" scope="col">
                            <span class="UserInvitation__label">
                              {{ messages.common.headList.status }}
                            </span>
                          </th>
                          <td
                            class="UserInvitation__item UserInvitation__status"
                          >
                            <span>{{
                              item.status
                                ? userStatusText[item.status] || '-'
                                : '-'
                            }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div class="UserInvitation__userSubTitle">
                      <div class="UserInvitation__label">
                        <Heading
                          class="UserInvitation__info"
                          type="tertiary"
                          level="2"
                          :heading="messages.user.userInvitation.licenseInfo"
                        />
                        <TypeLabel :isRequired="false" />
                      </div>
                      <span v-if="mode !== 'VIEW'">
                        {{ messages.user.userInvitation.info.license }}</span
                      >
                    </div>
                    <table class="UserInvitation__table">
                      <tbody>
                        <ul class="UserInvitation__multipleDropDownDiv">
                          <li
                            v-for="(service, serviceIndex) in serviceSettings"
                            :key="serviceIndex"
                            class="UserInvitation__multipleDropDown"
                          >
                            <tr>
                              <th class="UserInvitation__heading" scope="col">
                                <span class="UserInvitation__label">
                                  {{ getServiceNameById(service.serviceId) }}
                                </span>
                              </th>
                              <td class="UserInvitation__item">
                                <template
                                  v-if="mode == 'EDIT' || mode == 'ADD'"
                                >
                                  <div
                                    v-if="
                                      service.licenses &&
                                      service.licenses.length > 0
                                    "
                                  >
                                    <ul>
                                      <li
                                        v-for="(
                                          targetLicense, targetLicenseIndex
                                        ) in item.licenseIds"
                                        :key="targetLicenseIndex"
                                        class="UserInvitation__dropDownDisplayList"
                                      >
                                        <!-- Check if serviceId is 'pro' before rendering the DropDown and CancelButton -->
                                        <template
                                          v-if="
                                            targetLicense.serviceId ==
                                            service.serviceId
                                          "
                                        >
                                          <div
                                            class="UserInvitation__dropDownList"
                                          >
                                            <DropDown
                                              id="license"
                                              :value="
                                                item.licenseIds[
                                                  targetLicenseIndex
                                                ].roleId
                                              "
                                              class="UserInvitation__dropDown"
                                              :options="
                                                item.licenseOptions?.[
                                                  service.serviceId
                                                ] || []
                                              "
                                              :placeholder="
                                                messages.user.userInvitation
                                                  .placeholder.unSelected
                                              "
                                              :isError="
                                                (item.validate?.licenseError?.[
                                                  targetLicenseIndex
                                                ] ?? '') !== ''
                                              "
                                              @change="
                                                onClickChangeLicense(
                                                  $event,
                                                  index,
                                                  serviceIndex,
                                                  targetLicenseIndex
                                                )
                                              "
                                            />

                                            <CancelButton
                                              @click="
                                                onClickRemoveLicense(
                                                  index,
                                                  targetLicenseIndex
                                                )
                                              "
                                            />
                                          </div>
                                          <span
                                            class="UserInvitation__errorText"
                                          >
                                            {{
                                              item.validate?.licenseError?.[
                                                targetLicenseIndex
                                              ]
                                            }}
                                          </span>
                                        </template>
                                      </li>
                                    </ul>
                                  </div>
                                  <div v-else>
                                    <DropDown
                                      id="license"
                                      value="noContract"
                                      class="UserInvitation__dropDown"
                                      :options="[
                                        {
                                          text: planStatusLabel.noContract,
                                          value: 'noContract',
                                        },
                                      ]"
                                      :isDisabled="true"
                                    />
                                  </div>
                                  <Button
                                    class="UserInvitation__addButton"
                                    tag="button"
                                    type="button"
                                    dynamics="secondary"
                                    :label="messages.common.add"
                                    icon-name="Add"
                                    :isDisabled="
                                      service.licenses.length === 0 ||
                                      service.licenses.length <=
                                        formStateReact.value[
                                          index
                                        ].licenseIds.filter(
                                          license =>
                                            license.serviceId ===
                                            service.serviceId
                                        ).length
                                    "
                                    @click="
                                      onClickAddLicense(index, serviceIndex)
                                    "
                                  />
                                </template>
                                <!-- </li>
                            </ul> -->
                                <template v-else>
                                  <div
                                    v-if="
                                      formStateReact.value[
                                        index
                                      ].licenseIds.filter(
                                        license =>
                                          license.serviceId ===
                                          service.serviceId
                                      ).length > 0
                                    "
                                  >
                                    <ul>
                                      <li
                                        v-for="(
                                          targetLicense, targetLicenseIndex
                                        ) in item.licenseIds"
                                        :key="targetLicenseIndex"
                                        class="UserInvitation__dropDownDisplayList"
                                      >
                                        <!-- Check if serviceId is 'pro' before rendering the DropDown and CancelButton -->
                                        <template
                                          v-if="
                                            targetLicense.serviceId ==
                                            service.serviceId
                                          "
                                        >
                                          <span>
                                            {{
                                              getLicenseText(
                                                item.licenseIds[
                                                  targetLicenseIndex
                                                ].roleId,
                                                service.licenses
                                              )
                                            }}
                                          </span>
                                        </template>
                                      </li>
                                    </ul>
                                  </div>
                                  <div v-else>
                                    <span>{{
                                      planStatusLabel.noContract
                                    }}</span>
                                  </div>
                                </template>
                              </td>
                            </tr>
                          </li>
                        </ul>
                      </tbody>
                    </table>
                    <div
                      class="UserInvitation__userSubTitle"
                      v-if="mode !== 'ADD'"
                    >
                      <div class="UserInvitation__label">
                        <Heading
                          class="UserInvitation__info"
                          type="tertiary"
                          level="2"
                          :heading="
                            messages.user.userInvitation.passwordSettings
                          "
                        />
                      </div>
                      <table class="UserInvitation__table">
                        <tbody>
                          <tr>
                            <th class="UserInvitation__heading" scope="col">
                              <span class="UserInvitation__label">
                                {{ messages.user.userInvitation.password }}
                              </span>
                            </th>
                            <td class="UserInvitation__item">
                              <Button
                                tag="button"
                                dynamics="tertiaryBlack"
                                :label="messages.user.userInvitation.reset"
                                :isDisabled="mode === 'VIEW'"
                                @click="onClickPasswordReset"
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </li>
              </ul>
            </template>
            <div v-if="mode == 'ADD'" class="UserInvitation__userAddButtonDiv">
              <Button
                class="UserInvitation__userAddButton"
                tag="button"
                dynamics="secondary"
                icon-name="Add"
                :isDisabled="formStateList.length >= 10"
                @click="onClickAddUser"
              />
            </div>
          </section>
        </div>
      </Card>
    </main>
  </section>
</template>

<script lang="ts">
export default {
  name: 'UserInvitation',
};
</script>

<script lang="ts" setup>
import Button from '@spectee/components/Atoms/Button/index.vue';
import CancelButton from '@spectee/components/Atoms/CancelButton/index.vue';
import Card from '@spectee/components/Atoms/Card/index.vue';
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import TextField from '@spectee/components/Atoms/TextField/index.vue';
import TagInput from '@spectee/components/Atoms/TagInput/index.vue';
import TypeLabel from '@spectee/components/Atoms/TypeLabel/index.vue';
import DropDown from '@spectee/components/Atoms/DropDown/index.vue';
import TableSkeletonItem from '@spectee/components/Molecules/TableSkeletonItem/index.vue';
import { watch, ref, computed, toRefs } from 'vue';
import { useInviteMultipleUsers } from '@spectee/graphql/mutations/inviteMultipleUsers';
import { useSendPasswordResetMail } from '@spectee/graphql/mutations/sendPasswordResetMail';
import { useOverlayLoading } from '@spectee/composables/useOverlayLoading';
import {
  getServiceNameById,
  getLicenseNameByServiceIdAndLicenseKey,
} from '@spectee/ts/utils';
import { planStatusLabel } from '@spectee/const/planStatus';
import {
  AuthorityName,
  InviteMultipleUsersInput,
  Message,
  InvitationBasicOutput,
  ServiceSettings,
  LicenseValue,
} from '@spectee/graphql/api';
import { lengthValidate } from '@spectee/ts/validation';
import { useUpdateUser } from '@spectee/graphql/mutations/updateUserLatest';
import { userStatusText, UserStatus } from '@spectee/ts/userStatus';
import { commonMessage } from '@spectee/const/messageContents';
import { mailValidate } from '@spectee/ts/validation';
import messages from '../../../../messages.json';
import { isNotNullish } from '@spectee/ts/isNotNullish';
import { UserItem } from '@spectee/types/atoms';

type Props = {
  mode: string;
  isSkeleton: boolean;
  userData: UserItem;
  invitationBasicDetails: any;
  isEditCancel?: boolean;
  activeUsers?: string[];
};
const props = defineProps<Props>();

const { userData, invitationBasicDetails, isSkeleton, activeUsers } =
  toRefs(props);
const basicData = ref<InvitationBasicOutput>(invitationBasicDetails.value);
const isInvalidMail = ref<boolean[]>([]);

const emit = defineEmits([
  'update:inviteSuccess',
  'update:updateSuccess',
  'update:passwordResetSuccess',
]);

const { mutate: updateUser, loading: updateUserLoading } = useUpdateUser();

useOverlayLoading('updateUserLoading', updateUserLoading);

const { mutate: inviteMultipleUsers, loading: mutationInviteUsersLoading } =
  useInviteMultipleUsers();

useOverlayLoading('mutationInviteUsersLoading', mutationInviteUsersLoading);

const { mutate: sendPasswordResetMail, loading: passwordResetMailLoading } =
  useSendPasswordResetMail();

useOverlayLoading('passwordResetMailLoading', passwordResetMailLoading);

type LicenseComponentType = { serviceId: string; roleId?: string };
type DropDownType = {
  value: string | undefined;
  text: string | undefined;
  message?: string;
  isAddMsg?: boolean;
  disabled?: boolean;
};
type LicenseMap = {
  [serviceId: string]: DropDownType[];
};
type Validation = {
  displayNameError?: string;
  emailError?: string;
  authorityError?: string;
  licenseError?: string[];
  groupIdsError?: string[];
};

type FormState = {
  userId?: string;
  userName?: string;
  displayName?: string;
  emailIds: string[];
  authority: string;
  licenseIds: LicenseComponentType[];
  groupIds: DropDownType[];
  errorText: string;
  validate: Validation;
  status?: UserStatus;
  groupOptions?: DropDownType[];
  licenseOptions?: LicenseMap;
  authorityOptions?: DropDownType[];
  createdAt?: string;
};

const formStateList = ref<FormState[]>([]);

const formStateReact = computed(() => {
  return formStateList;
});

type ServiceSettingsType = {
  serviceId: string;
  licenses: DropDownType[];
};

type QuantityDetailsType = {
  availableQuantity: number;
  usedQuantity: number;
};
type QuantityDetailsMap = {
  [key: string]: QuantityDetailsType;
};

const authorityQuantityDetails: QuantityDetailsMap = {};
const licenseQuantityDetails: QuantityDetailsMap = {};
const serviceSettings = ref<ServiceSettingsType[]>([]);
const groupOptions = ref<DropDownType[]>([]);
const authority = ref<DropDownType[]>([]);

const setServiceSettings = () => {
  serviceSettings.value = (basicData.value?.serviceSettings ?? []).map(
    (service: ServiceSettings | undefined) => ({
      serviceId: service?.serviceId ?? '',
      licenses:
        service?.licenses
          .filter((license: LicenseValue) => isNotNullish(license.id)) // Filter out invalid license ids
          .map((license: LicenseValue) => {
            licenseQuantityDetails[license.id] = {
              availableQuantity: license.quantity - (license.usedQuantity ?? 0),
              usedQuantity: 0,
            };

            return {
              text: getLicenseNameByServiceIdAndLicenseKey(
                service?.serviceId ?? '',
                license?.key ?? ''
              ),
              value: license?.id ?? '',
              message: messages.user.userInvitation.error.limitReached,
            };
          }) ?? [],
    })
  );
};

const setGroupOptions = () => {
  groupOptions.value =
    (basicData.value?.groups ?? []).map(group => ({
      text: group?.name,
      value: group?.id,
    })) ?? [];
};

const setAuthority = () => {
  authority.value =
    (basicData.value?.authority ?? [])
      .filter(isNotNullish)
      .map(authorityVal => {
        authorityQuantityDetails[authorityVal.name] = {
          availableQuantity:
            (authorityVal.totalQuantity ?? 0) -
            (authorityVal.usedQuantity ?? 0),
          usedQuantity: 0,
        };
        return {
          value: authorityVal?.name,
          text: authorityVal?.displayName,
          message: messages.user.userInvitation.error.limitReached,
        };
      }) ?? [];
};

const getAuthorityText = (value: string) => {
  return (
    authority.value?.find(authority => authority.value === value)?.text || ''
  );
};

const getLicenseText = (
  roleId: string | undefined,
  licenses: DropDownType[]
) => {
  return licenses.find(license => license.value === roleId)?.text || '';
};

const onClickAddGroup = (index: number) => {
  formStateList.value[index].groupIds.push({
    value: '',
    text: '',
  });
};

const onClickRemoveGroup = (index: number, groupIndex: number) => {
  const user = formStateList.value[index];
  user.groupIds.splice(groupIndex, 1);
  // Remove errors from corresponding component
  if (user.validate?.groupIdsError) {
    user.validate.groupIdsError.splice(groupIndex, 1);
  }
  updateGroupOptions(user);
};

const updateGroupOptions = (user: FormState) => {
  user.groupOptions?.forEach(group => {
    const groupExists = user.groupIds.some(
      idObj => idObj.value === group.value
    );
    group.disabled = groupExists;
  });
};

const prepareLicenseOptions = (user: FormState) => {
  const serviceSettingValues = deepCopy(serviceSettings.value);
  const licenseOptions: LicenseMap = {};
  serviceSettingValues?.forEach((service: ServiceSettingsType) => {
    // Check if the license count is greater than 0
    if (service.licenses.length > 0) {
      const selectedLicenseIds =
        user.licenseIds.map(license => license.roleId) ?? [];
      service.licenses.forEach(license => {
        if (isNotNullish(license.value)) {
          const quantityDetails = licenseQuantityDetails[license.value];
          const isLimitReached =
            quantityDetails.availableQuantity <= quantityDetails.usedQuantity;
          license.disabled =
            isLimitReached || selectedLicenseIds.includes(license.value);
          license.isAddMsg = isLimitReached;
        }
      });
      licenseOptions[service.serviceId] = service.licenses;
    }
  });
  user['licenseOptions'] = licenseOptions;
};

const prepareAuthorityOptions = (user: FormState) => {
  const authorityOptions = deepCopy(authority.value);
  user['authorityOptions'] = authorityOptions.map((option: DropDownType) => {
    if (isNotNullish(option.value) && option.value !== 'viewer') {
      const quantityDetails = authorityQuantityDetails[option.value];
      const isLimitReached =
        quantityDetails.availableQuantity <= quantityDetails.usedQuantity;
      option.disabled = isLimitReached || user.authority === option.value;
      option.isAddMsg = isLimitReached;
    }
    return option;
  });
};

/**
 * Check the Group array and prepare initial group for screen display
 */
function prepareInitialGroup(): DropDownType[] {
  const initialGroup: DropDownType[] = [];
  const groupValues = groupOptions.value;
  // Check if the license count is greater than 0
  if (groupValues?.length > 0) {
    // Add the first group value for show dropdown
    initialGroup.push({
      value: groupValues[0].value,
      text: groupValues[0].text,
    });
  } else {
    initialGroup.push({
      value: '',
      text: '',
    });
  }
  return initialGroup;
}

const prepareGroupOptions = (user: FormState) => {
  user['groupOptions'] = deepCopy(groupOptions.value);
  updateGroupOptions(user); //Disable already selected group
};

const initialData = (insertPostion: number) => {
  formStateList.value.splice(insertPostion, 0, {
    emailIds: [],
    authority: 'viewer',
    licenseIds: [],
    groupIds: prepareInitialGroup(),
    errorText: '',
    validate: {},
  });
  const user = formStateList.value[insertPostion];
  prepareAuthorityOptions(user);
  prepareGroupOptions(user);
  prepareLicenseOptions(user);
};

const deepCopy = (original: any) => {
  return JSON.parse(JSON.stringify(original));
};

const setUserData = (newUserData: UserItem) => {
  if (props.mode !== 'ADD' && newUserData) {
    const userData = {
      userId: newUserData.user.auth0UserId,
      userName: newUserData.user.name,
      displayName: newUserData.user.displayName,
      emailIds: [newUserData.user.email],
      authority: newUserData.user.authority.name,
      licenseIds: newUserData.user.licenses?.map(license => {
        const quantityDetails = licenseQuantityDetails[license.roleId];
        //In edit mode , we need to add the already used license also in available quantity
        quantityDetails.availableQuantity += 1;
        return {
          serviceId: license.serviceId,
          roleId: license.roleId,
        };
      }),
      groupIds: newUserData.user.groups?.map(group => ({
        value: group.id,
        text: group.name,
      })),
      createdAt: newUserData.createdAt,
      errorText: '',
      validate: {},
      status: newUserData.status,
    };
    authorityQuantityDetails[userData.authority].availableQuantity += 1;
    prepareAuthorityOptions(userData);
    prepareGroupOptions(userData);
    prepareLicenseOptions(userData);
    formStateList.value[0] = userData;
  }
};

watch(
  () => props.isSkeleton,
  async newVal => {
    if (!newVal) {
      basicData.value = invitationBasicDetails.value;
      setServiceSettings();
      setGroupOptions();
      setAuthority();
      if (props.mode == 'ADD') {
        initialData(0);
      } else {
        setUserData(userData.value);
      }
    }
  },
  { immediate: true } // Ensure it runs immediately after the first evaluation
);

watch(
  () => props.isEditCancel,
  newVal => {
    if (newVal) {
      setUserData(userData.value);
    }
  }
);

const updateMailId = (mailId: string, index: number) => {
  formStateList.value[index].emailIds = [mailId];
};
const onClickAddLicense = (userIndex: number, serviceIndex: number) => {
  formStateList.value[userIndex].licenseIds.push({
    serviceId: serviceSettings.value[serviceIndex].serviceId,
    roleId: '',
  });
  updateLicenseOptionsDisplay();
};

const onChangeGroup = (
  selectedValue: any,
  userIndex: number,
  groupIndex: number
) => {
  const user = formStateList.value[userIndex];
  user.groupIds[groupIndex] = {
    text:
      groupOptions.value?.find(group => group.value === selectedValue)?.text ||
      '-',
    value: selectedValue,
  };

  // Remove errors from corresponding component
  if (user.validate?.groupIdsError) {
    // Clear the error contents
    user.validate.groupIdsError[groupIndex] = '';
  }

  updateGroupOptions(user);
};

const onChangeAuthority = (selectedValue: any, userIndex: number) => {
  formStateList.value[userIndex].authority = selectedValue;

  updateAuthorityOptionsDisplay();
};

const onClickChangeLicense = (
  selectedValue: any,
  userIndex: number,
  serviceIndex: number,
  targetLicenseIndex: number
) => {
  const user = formStateList.value[userIndex];
  const serviceId = serviceSettings.value[serviceIndex].serviceId;

  user.licenseIds[targetLicenseIndex] = {
    serviceId,
    roleId: selectedValue,
  };

  updateLicenseOptionsDisplay();
};

const updateAuthorityOptionsDisplay = () => {
  let usedQuantity: Record<string, number> = {};

  //get  selected authority used quantity by iterating formStateList
  formStateList.value.forEach(user => {
    usedQuantity[user.authority] =
      (usedQuantity[user.authority] ?? 0) + user.emailIds.length;
  });

  //set authority option disable and add limit reached information
  formStateList.value.forEach(user => {
    const selectedAuthority = user.authority;
    (user.authorityOptions ?? [])
      .filter(option => option.value !== 'viewer')
      .forEach(option => {
        if (isNotNullish(option.value)) {
          const isLimitReached =
            (usedQuantity[option.value] ?? 0) >=
            authorityQuantityDetails[option.value].availableQuantity;
          option.disabled = isLimitReached;
          option.isAddMsg = isLimitReached;
        }
      });

    //set validation error
    if (
      selectedAuthority !== 'viewer' &&
      (usedQuantity[selectedAuthority] ?? 0) >
        authorityQuantityDetails[selectedAuthority].availableQuantity
    ) {
      user.validate.authorityError =
        messages.user.userInvitation.error.authorityLimit;
    } else if (user.validate?.authorityError) {
      user.validate.authorityError = '';
    }
  });

  //Store used quantity of each authority in authorityQuantityDetails.
  //Used to make authority option disable when we add new user panel by clicking add button
  Object.entries(authorityQuantityDetails).forEach(
    ([value, quantityDetails]) => {
      quantityDetails.usedQuantity = usedQuantity[value] ?? 0;
    }
  );
};

const updateLicenseOptionsDisplay = () => {
  let usedQuantity: Record<string, number> = {};
  let selectedLicenseIdList: Record<number, string[]> = {};

  //get each selected license's used quantity by iterating formStateList
  //iterating user panel
  formStateList.value.forEach((user, index) => {
    selectedLicenseIdList[index] = [];
    user.licenseIds //iterating license ids selected in  user panel
      .forEach(license => {
        if (isNotNullish(license.roleId)) {
          usedQuantity[license.roleId] =
            (usedQuantity[license.roleId] ?? 0) + user.emailIds.length; //get license used quantity
          selectedLicenseIdList[index].push(license.roleId); //Used to make license options disable
        }
      });
  });

  //set license option disable and add limit reached information
  //Iterating each user panel
  formStateList.value.forEach((user, index) => {
    //get license options of each service
    Object.values(user.licenseOptions ?? []).forEach(options => {
      //Iterate options
      options.forEach(option => {
        if (isNotNullish(option.value)) {
          const isLimitReached =
            (usedQuantity[option.value] ?? 0) >=
            licenseQuantityDetails[option.value].availableQuantity;
          option.disabled =
            isLimitReached ||
            selectedLicenseIdList[index].includes(option.value);
          option.isAddMsg = isLimitReached;
        }
      });
    });

    //Iterate license ids of user and set error if its used quantity exceeds the available quantity
    user.licenseIds.forEach((license, targetLicenseIndex) => {
      //Clear existing error
      removeLicenseError(user, targetLicenseIndex);
      if (license.roleId) {
        const quantityDetails = licenseQuantityDetails[license.roleId];
        //Set validation error
        if (usedQuantity[license.roleId] > quantityDetails.availableQuantity) {
          if (!user.validate.licenseError) {
            user.validate.licenseError = [];
          }
          user.validate.licenseError[targetLicenseIndex] =
            messages.user.userInvitation.error.licenseLimit;
        }
      }
    });
  });

  //Store used quantity of each license in licenseQuantityDetails.
  //Used to make license option disable when we add new user panel by clicking add button
  Object.values(formStateList.value[0].licenseOptions ?? []).forEach(
    options => {
      //Iterate options of each service
      options.forEach(option => {
        if (isNotNullish(option.value)) {
          licenseQuantityDetails[option.value].usedQuantity =
            usedQuantity[option.value] ?? 0;
        }
      });
    }
  );
};

const onClickRemoveLicense = (
  userIndex: number,
  targetLicenseIndex: number
) => {
  const user = formStateList.value[userIndex];
  removeLicenseError(user, targetLicenseIndex);

  user.licenseIds.splice(targetLicenseIndex, 1);

  updateLicenseOptionsDisplay();
};

const removeLicenseError = (user: FormState, targetLicenseIndex: number) => {
  if (user.validate?.licenseError) {
    user.validate.licenseError[targetLicenseIndex] = '';
  }
};

const onClickAddUser = () => {
  initialData(formStateList.value.length);
};

const onClickRemove = (index: number) => {
  formStateList.value.splice(index, 1);
  updateLicenseOptionsDisplay();
  updateAuthorityOptionsDisplay();
};

const onClickPasswordReset = async () => {
  const user = formStateList.value[0];
  const res = await sendPasswordResetMail({
    userId: user.userId,
    email: user.emailIds[0],
  });
  const isPasswordRestSuccess =
    res &&
    res['data'] &&
    res['data']['sendPasswordResetMail'] === Message.success;
  emit('update:passwordResetSuccess', isPasswordRestSuccess);
};

const validateDisplayName = (
  name: string | undefined,
  index: number
): boolean => {
  const isEmpty = !name || name.trim() === '';
  if (isEmpty) {
    formStateList.value[index].validate.displayNameError =
      commonMessage.requiredField(messages.common.headList.displayName);
    return true;
  }
  const isInvalidLength = !lengthValidate(name, 50);
  formStateList.value[index].validate.displayNameError = isInvalidLength
    ? commonMessage.lengthError(50)
    : '';
  return isInvalidLength;
};

const setInvalidMailError = async (index: number, isInvalid: boolean) => {
  isInvalidMail.value[index] = isInvalid;
  validateEmail(formStateList.value[index].emailIds, index);
};

//Validate mail in  edit mode
const validateEmailId = () => {
  let emailIds = formStateList.value[0].emailIds; //In edit mode, we will have only one user
  if (emailIds[0]) {
    isInvalidMail.value[0] = !mailValidate(emailIds[0]); //In edit mode, we will have only one mail id
  } else {
    emailIds = [];
    isInvalidMail.value[0] = false;
  }
  validateEmail(emailIds, 0);
};

const validateEmail = (emailIds: string[], index: number): boolean => {
  // const pattern = /[\w\d_-]+@[\w\d_-]+\.[\w\d._-]+/;
  updateLicenseOptionsDisplay();
  updateAuthorityOptionsDisplay();

  const isInvalid = isInvalidMail.value[index];
  const activeUsersList = Array.isArray(activeUsers?.value)
    ? activeUsers.value.map(activeUserEmail =>
        String(activeUserEmail).toLowerCase()
      )
    : [];

  // Find duplicate active user emailIDs
  const duplicateActiveEmails: string[] = (emailIds ?? [])
    .filter((email: string) => Boolean(email))
    .filter((email: string) =>
      activeUsersList.includes(String(email).toLowerCase())
    );

  if (emailIds.length === 0 && !isInvalid) {
    formStateList.value[index].validate.emailError =
      commonMessage.requiredField(messages.common.headList.email);
  } else if (emailIds.length > 10) {
    formStateList.value[index].validate.emailError =
      commonMessage.emailMaxLimitError(10);
  } else if (emailIds.some(id => id.length > 60)) {
    formStateList.value[index].validate.emailError =
      messages.user.userInvitation.error.emailLength;
  }
  // Only check duplicates when in ADD mode
  else if (props.mode === 'ADD' && duplicateActiveEmails.length > 0) {
    formStateList.value[index].validate.emailError = `${
      messages.user.userInvitation.error.activeUserEmail
    }  ${duplicateActiveEmails.join(', ')}`;
  } else if (isInvalid) {
    formStateList.value[index].validate.emailError = commonMessage.emailError;
  } else {
    // メール形式に問題なければエラーメッセージとフラグを初期値に戻す
    formStateList.value[index].validate.emailError = '';
    return false;
  }
  return true;
};

const onClickUpdate = () => {
  // If there is no error , then save the data
  if (!validateFormStateList()) {
    updateUserData();
  }
};

const updateUserData = async () => {
  const updateItem = formStateList.value[0];
  const params = {
    auth0UserId: updateItem.userId,
    updateItem: {
      displayName: updateItem.displayName,
      email: updateItem.emailIds[0],
      authorityName: updateItem.authority,

      licenseIds: updateItem.licenseIds
        .filter(license => isNotNullish(license.roleId))
        .map(license => license.roleId),

      groupIds: updateItem.groupIds
        .filter(group => isNotNullish(group.value))
        .map(group => group.value),
    },
  };
  try {
    const resp = await updateUser({ input: params });
    const isUpdateSuccess =
      resp && !Object.prototype.hasOwnProperty.call(resp, 'errors');
    emit('update:updateSuccess', isUpdateSuccess);
  } catch (e) {
    emit('update:updateSuccess', false);
  }
};

const validateFormStateList = (): boolean => {
  let hasErrors = false; // Flag to track if there are any errors
  formStateList.value.forEach((formState, index) => {
    if (props.mode === 'EDIT') {
      if (validateDisplayName(formState.displayName, index)) hasErrors = true;
    }
    // Check if email exists and update emailError
    if (props.mode === 'EDIT') {
      if (!formState.emailIds[0]) {
        formState.emailIds = [];
      } else {
        isInvalidMail.value[0] = !mailValidate(formState.emailIds[0]);
      }
    }
    if (validateEmail(formState.emailIds, index)) hasErrors = true;

    // Check if authority exists and update authorityError
    const authorityEmpty = !basicData.value?.authority;
    if (authorityEmpty || !formState.authority) {
      formState.validate.authorityError = commonMessage.requiredField(
        messages.common.headList.authority
      );
      hasErrors = true;
    } else if (formState.validate.authorityError) {
      hasErrors = true;
    } else {
      formState.validate.authorityError = '';
    }

    formState.licenseIds.forEach((license, licenseIndex) => {
      // Check if roleId is missing or undefined
      if (!license.roleId) {
        // If roleId is missing, add an error message for this license in validate.licenseIds
        if (!formState.validate.licenseError) {
          formState.validate.licenseError = [];
        }

        // Set the error message for this license
        formState.validate.licenseError[licenseIndex] =
          commonMessage.requiredField(messages.user.userInvitation.license);
        hasErrors = true;
      } else if (
        formState.validate.licenseError &&
        formState.validate.licenseError[licenseIndex]
      ) {
        hasErrors = true;
        // formState.validate.licenseError[licenseIndex] = '';
      }
    });

    formState.groupIds.forEach((group, groupIndex) => {
      if (group.value === '' || group.text === '') {
        if (!formState.validate.groupIdsError) {
          formState.validate.groupIdsError = [];
        }

        // Add an error message to the corresponding position in groupIdsError
        formState.validate.groupIdsError[groupIndex] =
          commonMessage.requiredField(messages.common.headList.groupName);
        hasErrors = true;
      } else {
        // Clear the error message if roleId is present
        if (
          formState.validate.groupIdsError &&
          formState.validate.groupIdsError[groupIndex]
        ) {
          formState.validate.groupIdsError[groupIndex] = '';
        }
      }
    });
  });
  return hasErrors;
};

const onClickSend = () => {
  // If there is no error , then save the data
  if (!validateFormStateList()) {
    createUserInvitation();
  }
};

const createUserInvitation = async () => {
  const params: InviteMultipleUsersInput = {
    inviteUsers: formStateList.value.map(formState => ({
      authority: formState.authority as AuthorityName,
      email: formState.emailIds,
      groupIds: formState.groupIds
        .map(group => group.value)
        .filter(isNotNullish),
      licenseIds: formState.licenseIds
        .map(license => license.roleId)
        .filter(isNotNullish),
    })),
  };

  let isInviteSuccess = false;
  let failedIds: string[] = [];

  try {
    const resp = await inviteMultipleUsers({ input: params });
    const inviteRes = resp?.data?.inviteMultipleUsers;

    failedIds = inviteRes?.failedIds ?? [];
    if (
      inviteRes &&
      inviteRes.statusCode === Message.success &&
      failedIds.length === 0
    ) {
      isInviteSuccess = true;
    }
  } catch (e) {
    isInviteSuccess = false;
    failedIds = [];
  }
  emit('update:inviteSuccess', { isInviteSuccess, failedIds });
};

defineExpose({
  onClickSend,
  onClickUpdate,
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.UserInvitation {
  &__note {
    margin-top: $service-unit * 6;
  }

  & &__card {
    box-shadow: unset;
  }

  &__info {
    font-size: $service-font-size-p;
  }

  &__userTitle {
    display: flex;
    justify-content: space-between;
  }

  &__userSubTitle {
    margin-top: $service-unit * 2;
    gap: $service-unit * 2;
    display: flex;
    flex-direction: column;
  }

  &__list {
    margin-top: $service-unit * 3;
    padding: $service-unit * 3;
  }

  &__list--editMode {
    border: none !important;
    margin-top: 0;
    padding: 0;
  }

  &__table {
    width: 100%;
    table-layout: fixed;
    border-spacing: 0 $service-unit * 3;
    border-collapse: separate;
  }

  &__heading {
    @include text-title;

    width: $service-unit * 45;
    white-space: normal; // Allow text wrapping
    word-break: break-word; // Handle longer words appropriately
  }

  &__item {
    @include text-data;
    padding: 0 $service-unit * 3;
  }

  &__note {
    position: relative;
    top: $service-unit;
  }

  &__status {
    color: $color-green-60;
  }
  &__multipleDropDownDiv {
    display: flex;
    flex-direction: column;
  }

  &__dropDownList {
    display: flex;
    margin: $service-unit 0;
    align-items: center;
    gap: $service-unit;
  }

  &__label {
    display: flex;
    column-gap: $service-unit;
    align-items: center;
  }

  & &__dropDown {
    width: $service-unit * 46;
    min-width: $service-unit * 45;
  }

  &__addButton {
    margin-top: $service-unit * 3;
    padding: $service-unit $service-unit * 3;
  }

  &__userAddButtonDiv,
  &__list {
    border: 1px solid #bfbfbf;
    border-radius: 2px;
  }

  &__userAddButtonDiv {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: $service-unit * 3;
  }

  & &__userAddButton {
    background-color: $color-white-100;
    box-shadow: unset;
  }

  & &__modal {
    text-align: center;
  }

  &__errorText {
    display: block;
    margin-top: $service-unit;
    font-size: $service-font-size-pp;
    line-height: $service-line-height-mf;
    color: $color-red-70;
    text-align: left;
  }

  @media (max-width: 767px) {
    &__heading {
      font-size: $service-font-size-mp;
      width: $service-unit * 41;
    }
    &__list {
      min-width: $service-unit * 125;
    }
    &__info,
    &__note {
      font-size: $service-font-size-pp;
    }
  }
}
</style>
